import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectPayPalBtnColor, selectTopProps } from '../../../../selectors';
import { logGeneralError } from '../../../../utils/sentry';
import { PayPalController } from '../controller';
import s from '../styles.scss';

export interface Props {
  fundingSource: string;
  widgetRootWrapperDh: string;
  createOrder: InstanceType<typeof PayPalController>['createOrder'];
  onShippingChange: InstanceType<typeof PayPalController>['onShippingChange'];
  onClick: InstanceType<typeof PayPalController>['onClick'];
  onApprove: InstanceType<typeof PayPalController>['onApprove'];
  onCancel(arg: { orderID: string }): void;
}

const onError = (e) => {
  logGeneralError({
    message: 'PayPal onError cb',
    payload: e,
    fingerprint: ['onError cb'],
  });
};

export const PayPalButton: React.FC<Props> = (props) => {
  const topProps = useSelector(selectTopProps);
  const dataHook = `ecw-button-wrapper-${props.fundingSource}`;
  const color = useSelector(selectPayPalBtnColor);
  const button = React.useRef(null);
  const buttonsContainerRef = React.useRef<HTMLDivElement>(null);

  function closeButtonsComponent() {
    if (button.current !== null) {
      button.current.close();
    }
  }
  // useEffect hook for rendering the buttons
  // originaly is taken from https://github.com/paypal/react-paypal-js/blob/main/src/components/PayPalButtons.tsx#L55
  useEffect(() => {
    button.current = window.paypal.Buttons({
      style: {
        color: props.fundingSource === window.paypal.FUNDING.VENMO ? 'blue' : color,
        shape: topProps.buttonStyle.shape,
        tagline: false,
        label: 'checkout',
        height: topProps.buttonStyle.height,
      },
      fundingSource: props.fundingSource,
      createOrder: props.createOrder,
      onShippingChange: props.onShippingChange,
      onClick: props.onClick,
      onApprove: props.onApprove,
      onError,
      onCancel: props.onCancel,
    });

    // only render the button when eligible
    if (button.current.isEligible() === false) {
      return closeButtonsComponent;
    }

    if (buttonsContainerRef.current === null) {
      return closeButtonsComponent;
    }

    button.current.render(buttonsContainerRef.current).catch((err) => {
      // component failed to render, possibly because it was closed or destroyed.
      if (buttonsContainerRef.current === null || buttonsContainerRef.current.children.length === 0) {
        // paypal button is no longer in the DOM, we can safely ignore the error
        return;
      }
      console.error(new Error(`Failed to render <PayPalButtons /> component. ${err}`));
    });
    return closeButtonsComponent;
  }, [topProps.theme, topProps.buttonStyle.height, topProps.buttonStyle.shape]);

  return (
    <div
      ref={buttonsContainerRef}
      data-hook={dataHook}
      className={s.wrapper}
      style={{ height: topProps.buttonStyle.height }}
    ></div>
  );
};
